import React from "react";
import LegalPage from "../components/LegalPage";

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 marker:text-[#1d4ed8]/60">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const SECTIONS = [
  {
    id: "introduction",
    heading: "1. Introduction",
    body: [
      <p key="1a">Welcome to Medily Ltd (“Medily”, “we”, “our”, or “us”).</p>,
      <p key="1b">
        Your privacy is important to us. This Privacy Policy explains how we collect, use,
        store, protect and share your personal information when you:
      </p>,
      <List
        key="1c"
        items={[
          "visit our website;",
          "create a Medily account;",
          "purchase a subscription or Mock MMI;",
          "use Maya;",
          "access our Resources;",
          "contact us; or",
          "otherwise interact with our services.",
        ]}
      />,
      <p key="1d">Please read this Privacy Policy alongside our:</p>,
      <List key="1e" items={["Terms & Conditions", "Cookie Policy", "Refund & Cancellation Policy"]} />,
      <p key="1f">
        By using Medily, you acknowledge that your personal information will be processed in
        accordance with this Privacy Policy.
      </p>,
    ],
  },
  {
    id: "who-we-are",
    heading: "2. Who We Are",
    body: [
      "Medily Ltd is the data controller responsible for your personal information.",
      "Email: info@medily.co",
    ],
  },
  {
    id: "information-we-collect",
    heading: "3. Information We Collect",
    body: [
      <p key="3-intro">Depending on how you use Medily, we may collect the following information.</p>,

      <h3 key="3.1h" className="text-[17px] font-semibold text-slate-900 mt-4">3.1 Account Information</h3>,
      <p key="3.1p">When you create an account, we may collect:</p>,
      <List key="3.1l" items={["Full name", "Email address", "Password (encrypted)", "Account ID", "Subscription status"]} />,

      <h3 key="3.2h" className="text-[17px] font-semibold text-slate-900 mt-4">3.2 Payment Information</h3>,
      <p key="3.2p1">Payments are processed securely through Stripe.</p>,
      <p key="3.2p2">We do not store:</p>,
      <List key="3.2l1" items={["debit card numbers", "credit card numbers", "security codes"]} />,
      <p key="3.2p3">Stripe may provide us with limited payment information such as:</p>,
      <List
        key="3.2l2"
        items={["payment status", "subscription status", "transaction reference", "billing country"]}
      />,
      <p key="3.2p4">Please refer to Stripe’s Privacy Policy for further information.</p>,

      <h3 key="3.3h" className="text-[17px] font-semibold text-slate-900 mt-4">3.3 Maya Responses</h3>,
      <p key="3.3p1">When you use Maya we may collect:</p>,
      <List
        key="3.3l"
        items={[
          "interview answers",
          "selected university",
          "chosen station",
          "difficulty level",
          "feedback",
          "scores",
          "optimised answers",
          "timestamps",
        ]}
      />,
      <p key="3.3p2">
        This information allows Maya to provide personalised feedback and improve your learning
        experience.
      </p>,

      <h3 key="3.4h" className="text-[17px] font-semibold text-slate-900 mt-4">3.4 Resource Usage</h3>,
      <p key="3.4p">When using Resources we may collect:</p>,
      <List
        key="3.4l"
        items={["completed topics", "reading progress", "study time", "guide completion", "dashboard statistics"]}
      />,

      <h3 key="3.5h" className="text-[17px] font-semibold text-slate-900 mt-4">3.5 Mock MMI Information</h3>,
      <p key="3.5p">If you purchase a University-Specific Mock MMI we may collect:</p>,
      <List
        key="3.5l"
        items={[
          "selected university",
          "interview booking information",
          "interview attendance",
          "written interviewer feedback",
          "interview scores",
          "rescheduling information",
        ]}
      />,

      <h3 key="3.6h" className="text-[17px] font-semibold text-slate-900 mt-4">3.6 Communications</h3>,
      <p key="3.6p">If you contact us we may collect:</p>,
      <List key="3.6l" items={["emails", "support requests", "complaints", "survey responses", "feedback"]} />,

      <h3 key="3.7h" className="text-[17px] font-semibold text-slate-900 mt-4">3.7 Technical Information</h3>,
      <p key="3.7p">We automatically collect certain technical information including:</p>,
      <List
        key="3.7l"
        items={[
          "IP address",
          "browser type",
          "operating system",
          "device type",
          "approximate location",
          "referring website",
          "pages visited",
          "timestamps",
        ]}
      />,
    ],
  },
  {
    id: "special-category-data",
    heading: "4. Special Category Data",
    body: [
      "Medily does not intentionally request special category personal data.",
      "Please avoid including unnecessary sensitive information when submitting interview answers or contacting us.",
      "If sensitive information is provided, it will only be processed where necessary for providing our services or where permitted by law.",
    ],
  },
  {
    id: "how-we-use-your-information",
    heading: "5. How We Use Your Information",
    body: [
      <p key="5p">We use your information to:</p>,
      <List
        key="5l"
        items={[
          "create your account;",
          "authenticate your identity;",
          "provide Resources;",
          "provide Maya;",
          "deliver Mock MMIs;",
          "process payments;",
          "manage subscriptions;",
          "communicate with you;",
          "improve our services;",
          "monitor website performance;",
          "detect fraud;",
          "enforce our Terms;",
          "comply with legal obligations.",
        ]}
      />,
    ],
  },
  {
    id: "lawful-basis",
    heading: "6. Lawful Basis for Processing",
    body: [
      <p key="6-intro">Under UK GDPR we rely on one or more of the following lawful bases:</p>,
      <div key="6a" className="rounded-xl bg-[#f4f7fd] border border-[#dbe6ff] p-4">
        <p className="font-semibold text-slate-900">Contract</p>
        <p className="text-slate-600 mt-1">To provide the services you purchase.</p>
      </div>,
      <div key="6b" className="rounded-xl bg-[#f4f7fd] border border-[#dbe6ff] p-4">
        <p className="font-semibold text-slate-900">Legitimate Interests</p>
        <p className="text-slate-600 mt-1">To improve our services, prevent fraud and ensure website security.</p>
      </div>,
      <div key="6c" className="rounded-xl bg-[#f4f7fd] border border-[#dbe6ff] p-4">
        <p className="font-semibold text-slate-900">Legal Obligation</p>
        <p className="text-slate-600 mt-1">Where required by law.</p>
      </div>,
      <div key="6d" className="rounded-xl bg-[#f4f7fd] border border-[#dbe6ff] p-4">
        <p className="font-semibold text-slate-900">Consent</p>
        <p className="text-slate-600 mt-1">
          Where consent is required, including certain cookies and marketing communications.
        </p>
      </div>,
      <p key="6-out">You may withdraw consent at any time.</p>,
    ],
  },
  {
    id: "who-we-share-information-with",
    heading: "7. Who We Share Information With",
    body: [
      <p key="7-a">We only share personal information where necessary.</p>,
      <p key="7-b">This may include:</p>,
      <div key="7-list" className="space-y-3">
        {[
          ["Stripe", "To process payments."],
          ["Calendly", "To manage Mock MMI bookings."],
          ["Cloud Hosting Providers", "To securely host the Medily platform."],
          ["Email Providers", "To send account and transactional emails."],
          ["Analytics Providers", "To understand website performance where consent has been provided."],
          ["Professional Advisers", "Lawyers, accountants or insurers where necessary."],
          ["Government Authorities", "Where required by law."],
        ].map(([who, why]) => (
          <div key={who} className="rounded-xl bg-white border border-slate-100 p-4">
            <p className="font-semibold text-slate-900">{who}</p>
            <p className="text-slate-600 mt-1">{why}</p>
          </div>
        ))}
      </div>,
      <p key="7-c" className="font-medium text-slate-900">We never sell your personal information.</p>,
    ],
  },
  {
    id: "international-transfers",
    heading: "8. International Transfers",
    body: [
      "Some service providers may process information outside the United Kingdom.",
      "Where this occurs, appropriate safeguards will be implemented in accordance with UK GDPR.",
    ],
  },
  {
    id: "how-long-we-keep-information",
    heading: "9. How Long We Keep Information",
    body: [
      <p key="9a">We retain information only for as long as necessary.</p>,
      <p key="9b">Typical retention periods include:</p>,
      <div key="9-table" className="overflow-x-auto rounded-xl border border-slate-100">
        <table className="w-full text-[14.5px]">
          <thead className="bg-[#f4f7fd] text-slate-600">
            <tr>
              <th className="text-left font-semibold px-4 py-2.5">Data</th>
              <th className="text-left font-semibold px-4 py-2.5">Retention</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              ["Account information", "While account remains active"],
              ["Payment records", "As required by law"],
              ["Maya responses", "While account remains active unless deleted"],
              ["Interview feedback", "While account remains active"],
              ["Support requests", "Up to 6 years where appropriate"],
              ["Website analytics", "According to analytics provider settings"],
            ].map(([d, r]) => (
              <tr key={d}>
                <td className="px-4 py-2.5 text-slate-800">{d}</td>
                <td className="px-4 py-2.5 text-slate-600">{r}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
      <p key="9c">Where required by law, information may be retained for longer.</p>,
    ],
  },
  {
    id: "account-deletion",
    heading: "10. Account Deletion",
    body: [
      <p key="10a">
        You may request deletion of your account by contacting:{" "}
        <a href="mailto:info@medily.co" className="text-[#1d4ed8] font-medium hover:underline">
          info@medily.co
        </a>
      </p>,
      <p key="10b">Deleting your account may permanently remove:</p>,
      <List
        key="10c"
        items={[
          "Maya history",
          "interview scores",
          "progress",
          "Resources progress",
          "dashboard statistics",
        ]}
      />,
      <p key="10d">Some information may be retained where required by law.</p>,
    ],
  },
  {
    id: "data-security",
    heading: "11. Data Security",
    body: [
      <p key="11a">We implement appropriate technical and organisational measures including:</p>,
      <List
        key="11b"
        items={[
          "encrypted connections (HTTPS)",
          "secure authentication",
          "password hashing",
          "access controls",
          "secure hosting",
          "payment processing through Stripe",
          "regular software updates",
        ]}
      />,
      <p key="11c">
        Although we take reasonable steps to protect your information, no internet transmission
        can ever be guaranteed completely secure.
      </p>,
    ],
  },
  {
    id: "cookies",
    heading: "12. Cookies",
    body: [
      <p key="12a">We use cookies as explained in our Cookie Policy.</p>,
      <p key="12b">Cookies help us:</p>,
      <List
        key="12c"
        items={[
          "maintain secure sessions;",
          "remember preferences;",
          "improve website performance;",
          "understand website usage.",
        ]}
      />,
      <p key="12d">You can manage cookies through our Cookie Banner or your browser settings.</p>,
    ],
  },
  {
    id: "marketing",
    heading: "13. Marketing",
    body: [
      <p key="13a">We may occasionally send updates regarding:</p>,
      <List
        key="13b"
        items={["new Resources", "platform improvements", "new services", "important account information"]}
      />,
      <p key="13c">Marketing communications will only be sent where permitted by law.</p>,
      <p key="13d">You may unsubscribe at any time.</p>,
      <p key="13e">Transactional emails relating to your account or purchases cannot be opted out of.</p>,
    ],
  },
  {
    id: "childrens-privacy",
    heading: "14. Children’s Privacy",
    body: [
      "Medily is designed primarily for applicants preparing for university interviews.",
      "Users under 18 should ensure they understand this Privacy Policy and, where appropriate, seek guidance from a parent or guardian.",
    ],
  },
  {
    id: "your-rights",
    heading: "15. Your Rights",
    body: [
      <p key="15a">Under UK GDPR you may have the right to:</p>,
      <List
        key="15b"
        items={[
          "access your personal information;",
          "correct inaccurate information;",
          "request deletion;",
          "restrict processing;",
          "object to processing;",
          "request data portability;",
          "withdraw consent;",
          "lodge a complaint with the Information Commissioner’s Office (ICO).",
        ]}
      />,
      <p key="15c">Some rights are subject to legal exceptions.</p>,
    ],
  },
  {
    id: "automated-decision-making",
    heading: "16. Automated Decision-Making",
    body: [
      "Maya provides automated interview feedback using artificial intelligence.",
      "This feedback is intended solely for educational and practice purposes.",
      "Users should not rely on Maya as professional medical, educational or legal advice.",
      "Final admissions decisions are made solely by universities.",
    ],
  },
  {
    id: "third-party-websites",
    heading: "17. Third-Party Websites",
    body: [
      <p key="17a">Our website may contain links to third-party websites including:</p>,
      <List key="17b" items={["Stripe", "Calendly", "universities", "social media platforms"]} />,
      <p key="17c">We are not responsible for their privacy practices.</p>,
      <p key="17d">Users should read the privacy policies of those organisations.</p>,
    ],
  },
  {
    id: "changes-to-this-privacy-policy",
    heading: "18. Changes to This Privacy Policy",
    body: [
      "We may update this Privacy Policy periodically.",
      "The latest version will always be published on our website.",
      "Material changes may also be communicated by email or through your account where appropriate.",
    ],
  },
  {
    id: "contact-us",
    heading: "19. Contact Us",
    body: [
      "If you have questions about this Privacy Policy or your personal information, please contact:",
      "Medily Ltd",
      "Email: info@medily.co",
    ],
  },
  {
    id: "complaints",
    heading: "20. Complaints",
    body: [
      "If you are unhappy with how we handle your personal information, please contact us first so that we can attempt to resolve your concerns.",
    ],
  },
  {
    id: "compliance",
    heading: "21. Compliance",
    body: [
      <p key="21a">This Privacy Policy is intended to support Medily’s compliance with:</p>,
      <List
        key="21b"
        items={[
          "UK General Data Protection Regulation (UK GDPR)",
          "Data Protection Act 2018",
          "Privacy and Electronic Communications Regulations (PECR), where applicable",
          "other applicable UK data protection legislation",
        ]}
      />,
      <p key="21c">Nothing in this Privacy Policy limits your statutory rights under applicable law.</p>,
    ],
  },
];

const PrivacyPolicy = () => (
  <LegalPage
    title="Privacy Policy"
    subtitle="How Medily collects, uses, stores, protects and shares your personal information when you visit our website, create an account, purchase a subscription or Mock MMI, use Maya, access our Resources, contact us or otherwise interact with our services."
    sections={SECTIONS}
  />
);

export default PrivacyPolicy;
